import React, { useEffect, useRef, useState } from 'react';
import Hangul from 'hangul-js';
import Keyboard from 'react-simple-keyboard';
import { useLocation, useNavigate } from 'react-router-dom';
import './App.css';

const Modal = ({ message, onClose, confirmText = '확인' }) => {
    return (
        <div className="modal-overlay">
            <div className="modal-content">
                <p className="modal-message">{message}</p>
                <button onClick={onClose} className="modal-button">{confirmText}</button>
            </div>
        </div>
    );
};

function ResultPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { isCorrect, answer, rows, cols, clearTime = 0 } = location.state || { isCorrect: false, answer: '', rows: 3, cols: 3, clearTime: 0 };
    const isRankingMode = true;
    const nameInputRef = useRef(null);

    const [showEventForm, setShowEventForm] = useState(false);
    const [name, setName] = useState('');
    const [phone, setPhone] = useState('');
    const [activeInput, setActiveInput] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [modalInfo, setModalInfo] = useState({ show: false, message: '', onConfirm: null });

    const [texts, setTexts] = useState({
        resultSuccessTitle: '정답입니다!',
        resultSuccessSubtitle: '축하합니다. 이벤트에 참여해 주세요.',
        resultFailTitle: '아쉽습니다',
        resultFailSubtitle: '다시 한번 도전해보세요.',
        rankingSuccessTitle: '타임어택 완료!',
        rankingSuccessSubtitle: '기록을 랭킹에 등록해 보세요.',
        popupDuplicate: '이미 해당 연락처로 이벤트에 참여하셨습니다.',
        popupSuccess: '이벤트 참여가 완료되었습니다.',
        popupRankingSuccess: '랭킹 등록이 완료되었습니다.',
        popupRankingUpdate: '신기록 달성! 랭킹이 갱신되었습니다.',
        popupRankingFail: '기존 기록이 더 빠릅니다. 기록이 갱신되지 않았습니다.',
        popupInvalidPhone: '전화번호 형식이 올바르지 않습니다.',
        popupAgree: '광고성 정보 수신에 동의해야 참여할 수 있습니다.',
        popupPhoneHyphen: '하이픈(-)을 제외하고 숫자만 입력해 주세요.',
        popupPhoneLength: '전화번호 11자리를 정확히 입력해 주세요.',
        popupSubmitErrorPrefix: '오류가 발생했습니다: ',
        eventButtonText: '이벤트 참여하기',
        eventFormTitle: '이벤트 참여 정보 입력',
        eventNameLabel: '이름:',
        eventNamePlaceholder: '이름 입력',
        eventPhoneLabel: '연락처(하이픈 제외):',
        eventPhonePlaceholder: '01012345678',
        eventAgreeLabel: '광고성 정보 수신 동의',
        eventSubmitText: '제출',
        playAgainText: '다시하기',
        homeButtonText: '처음으로',
        modalConfirmText: '확인'
    });

    useEffect(() => {
        fetch('/api/config')
            .then(res => res.json())
            .then(data => {
                if (data.resultSuccessTitle) setTexts(prev => ({ ...prev, ...data }));
            })
            .catch(err => console.error('Failed to load config:', err));
    }, []);

    useEffect(() => {
        if (showEventForm && activeInput === 'name') {
            nameInputRef.current?.focus();
        }
    }, [showEventForm, activeInput]);

    const showModal = (message, onConfirm = null) => {
        setModalInfo({
            show: true,
            message,
            onConfirm: onConfirm || (() => setModalInfo(prev => ({ ...prev, show: false })))
        });
    };

    const handleModalClose = () => {
        const onConfirm = modalInfo.onConfirm;
        setModalInfo(prev => ({ ...prev, show: false }));
        if (onConfirm) onConfirm();
    };

    const handlePlayAgain = () => {
        navigate('/game', { state: { rows, cols, isRankingMode } });
    };

    const handleGoHome = () => {
        navigate('/');
    };

    const handleEventSubmit = (e) => {
        e.preventDefault();

        const trimmedName = name.trim();
        if (!trimmedName) {
            showModal('이름을 입력해 주세요.');
            return;
        }
        if (trimmedName.length > 6) {
            showModal('이름은 최대 6글자까지만 입력해 주세요.');
            return;
        }

        if (phone.includes('-')) {
            showModal(texts.popupPhoneHyphen);
            return;
        }
        if (!/^\d+$/.test(phone)) {
            showModal(texts.popupInvalidPhone || '전화번호는 숫자만 입력해 주세요.');
            return;
        }
        if (phone.length !== 11) {
            showModal(texts.popupPhoneLength);
            return;
        }
        if (!agreed) {
            showModal(texts.popupAgree || '광고성 정보 수신에 동의해야 참여할 수 있습니다.');
            return;
        }

        const apiUrl = isRankingMode ? '/api/ranking/register' : '/api/event/participate';
        const requestBody = isRankingMode
            ? { name: trimmedName, phone, answer, agreed, clearTime }
            : { name: trimmedName, phone, answer, agreed };

        fetch(apiUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(requestBody),
        })
        .then(async res => {
            if (res.ok) {
                const responseText = await res.text();
                const successMessage = isRankingMode
                    ? (responseText.includes('신기록') ? texts.popupRankingUpdate : texts.popupRankingSuccess)
                    : texts.popupSuccess;
                showModal(successMessage, () => navigate('/'));
            } else if (res.status === 409) {
                showModal(isRankingMode ? texts.popupRankingFail : texts.popupDuplicate, () => navigate('/'));
            } else {
                const text = await res.text();
                throw new Error(text);
            }
        })
        .catch(err => {
            console.error('Submission failed:', err);
            showModal(texts.popupSubmitErrorPrefix + err.message);
        });
    };

    const handleKeyboardPress = (button) => {
        if (!activeInput) return;

        if (button === '{bksp}') {
            if (activeInput === 'name') {
                setName((current) => {
                    const letters = Hangul.disassemble(current);
                    letters.pop();
                    return Hangul.assemble(letters);
                });
            } else {
                setPhone((current) => current.slice(0, -1));
            }
            return;
        }

        if (button === '{space}' && activeInput === 'name') {
            setName((current) => current.length < 6 ? current + ' ' : current);
            return;
        }

        if (button === '{enter}') {
            if (activeInput === 'name') {
                setActiveInput('phone');
            }
            return;
        }

        if (activeInput === 'name') {
            setName((current) => {
                const next = Hangul.assemble([...Hangul.disassemble(current), button]);
                return next.length <= 6 ? next : current;
            });
            return;
        }

        if (/^\d$/.test(button)) {
            setPhone((current) => (current + button).slice(0, 11));
        }
    };

    const keyboardLayout = activeInput === 'phone'
        ? {
            default: [
                '1 2 3',
                '4 5 6',
                '7 8 9',
                '0 {bksp}',
            ],
        }
        : {
            default: [
                'ㅂ ㅈ ㄷ ㄱ ㅅ ㅛ ㅕ ㅑ ㅐ ㅔ',
                'ㅁ ㄴ ㅇ ㄹ ㅎ ㅗ ㅓ ㅏ ㅣ',
                'ㅋ ㅌ ㅊ ㅍ ㅠ ㅜ ㅡ {bksp}',
                '{space} {enter}',
            ],
        };

    const title = isCorrect
        ? (isRankingMode ? texts.rankingSuccessTitle : texts.resultSuccessTitle)
        : texts.resultFailTitle;
    const subtitle = isCorrect
        ? (isRankingMode ? texts.rankingSuccessSubtitle : texts.resultSuccessSubtitle)
        : texts.resultFailSubtitle;

    return (
        <div className="page-container result-page">
            {modalInfo.show && <Modal message={modalInfo.message} onClose={handleModalClose} confirmText={texts.modalConfirmText} />}

            <div className="card">
                <h1 className="result-title">{title}</h1>
                <p className="result-subtitle">{subtitle}</p>

                {isCorrect && isRankingMode && (
                    <div className="clear-time">클리어 시간: {Number(clearTime).toFixed(2)}초</div>
                )}

                {isCorrect && !showEventForm && (
                    <button
                        onClick={() => {
                            setShowEventForm(true);
                            setActiveInput('name');
                        }}
                        className="action-button event-button"
                        style={{ marginBottom: '20px', backgroundColor: isRankingMode ? '#ff4b2b' : '#28a745' }}
                    >
                        {isRankingMode ? '랭킹 등록하기' : texts.eventButtonText}
                    </button>
                )}

                {showEventForm && (
                    <div className="event-form-container" style={{ marginBottom: '20px', textAlign: 'left', padding: '0 20px' }}>
                        <h3>{isRankingMode ? '랭킹 등록 정보 입력' : texts.eventFormTitle}</h3>
                        <form onSubmit={handleEventSubmit}>
                            <div className="form-group">
                                <label>{texts.eventNameLabel}</label>
                                <input
                                    ref={nameInputRef}
                                    type="text"
                                    maxLength={6}
                                    value={name}
                                    onChange={(e) => setName(e.target.value.slice(0, 6))}
                                    onFocus={() => setActiveInput('name')}
                                    placeholder={texts.eventNamePlaceholder}
                                    style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ccc', backgroundColor: 'white', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div className="form-group">
                                <label>{texts.eventPhoneLabel}</label>
                                <input
                                    type="tel"
                                    value={phone}
                                    onChange={(e) => setPhone(e.target.value)}
                                    onFocus={() => setActiveInput('phone')}
                                    placeholder={texts.eventPhonePlaceholder}
                                    style={{ width: '100%', padding: '8px', marginBottom: '10px', borderRadius: '5px', border: '1px solid #ccc', backgroundColor: 'white', boxSizing: 'border-box' }}
                                />
                            </div>
                            <div className="form-group" style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                                <input
                                    type="checkbox"
                                    id="agree"
                                    checked={agreed}
                                    onChange={(e) => setAgreed(e.target.checked)}
                                    style={{ marginRight: '10px' }}
                                />
                                <label htmlFor="agree" style={{ fontSize: '0.9rem' }}>{texts.eventAgreeLabel}</label>
                            </div>
                            <button type="submit" className="action-button" style={{ width: '100%' }}>{texts.eventSubmitText}</button>
                        </form>
                    </div>
                )}

                {showEventForm && activeInput && (
                    <div className="keyboard-container show">
                        <Keyboard
                            layout={keyboardLayout}
                            display={{
                                '{bksp}': '⌫',
                                '{enter}': '다음',
                                '{space}': '공백',
                            }}
                            onKeyPress={handleKeyboardPress}
                        />
                    </div>
                )}

                <div className="button-group">
                    <button onClick={handlePlayAgain} className="action-button">
                        {texts.playAgainText}
                    </button>
                    <button onClick={handleGoHome} className="secondary-button">
                        {texts.homeButtonText}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ResultPage;
